import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card } from '../common/Card';
import { allStores } from '../../data/mockData';

interface StorePerformanceData {
  storeName: string;
  streams: number;
  revenue: number;
  songs: number;
}

interface StorePerformanceChartProps {
  data?: StorePerformanceData[];
}

export const StorePerformanceChart: React.FC<StorePerformanceChartProps> = ({ data }) => {
  // Generate mock data if none provided
  const defaultData: StorePerformanceData[] = [
    { storeName: 'Spotify', streams: 2500000, revenue: 12500, songs: 45 },
    { storeName: 'Apple Music', streams: 1800000, revenue: 14400, songs: 42 },
    { storeName: 'YouTube Music', streams: 1200000, revenue: 3600, songs: 38 },
    { storeName: 'JioSaavn', streams: 900000, revenue: 2700, songs: 35 },
    { storeName: 'Amazon Music', streams: 650000, revenue: 4550, songs: 32 },
    { storeName: 'Deezer', streams: 450000, revenue: 2250, songs: 28 },
    { storeName: 'Tidal', streams: 320000, revenue: 2240, songs: 25 },
    { storeName: 'Gaana', streams: 280000, revenue: 840, songs: 22 }
  ];

  const chartData = (data || defaultData).map(store => ({
    ...store,
    streamsK: Math.floor(store.streams / 1000), // Convert to thousands
    storeName: store.storeName.length > 15 ? store.storeName.substring(0, 12) + '...' : store.storeName
  }));

  const formatTooltipValue = (value: number, name: string) => {
    if (name === 'streamsK') {
      return [`${(value * 1000).toLocaleString()}`, 'Streams'];
    }
    if (name === 'revenue') {
      return [`$${value.toLocaleString()}`, 'Revenue'];
    }
    return [value.toLocaleString(), name];
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white font-display">
          Store Performance
        </h3>
        <div className="flex items-center space-x-4 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-400 rounded-full" />
            <span className="text-gray-400">Streams</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-400 rounded-full" />
            <span className="text-gray-400">Revenue</span>
          </div>
        </div>
      </div>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis 
              dataKey="storeName"
              stroke="#9CA3AF"
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              yAxisId="streams"
              orientation="left"
              stroke="#9CA3AF"
              fontSize={12}
              tickFormatter={(value) => `${value}K`}
            />
            <YAxis 
              yAxisId="revenue"
              orientation="right"
              stroke="#9CA3AF"
              fontSize={12}
              tickFormatter={(value) => `$${value}`}
            />
            <Tooltip
              formatter={formatTooltipValue}
              labelFormatter={(label) => `Store: ${label}`}
              contentStyle={{
                backgroundColor: '#1F2937',
                border: '1px solid #374151',
                borderRadius: '8px',
                color: '#FFFFFF'
              }}
            />
            <Bar 
              yAxisId="streams"
              dataKey="streamsK" 
              fill="#60A5FA"
              radius={[2, 2, 0, 0]}
              opacity={0.8}
            />
            <Bar 
              yAxisId="revenue"
              dataKey="revenue" 
              fill="#34D399"
              radius={[2, 2, 0, 0]}
              opacity={0.8}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Store breakdown */}
      <div className="mt-6 pt-6 border-t border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {chartData.slice(0, 6).map((store, index) => (
            <div key={store.storeName} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center text-sm">
                  🎵
                </div>
                <div>
                  <p className="text-white font-medium text-sm">{store.storeName}</p>
                  <p className="text-gray-400 text-xs">{store.songs} songs</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-blue-400 text-sm font-medium">
                  {(store.streamsK * 1000).toLocaleString()}
                </p>
                <p className="text-green-400 text-xs">
                  ${store.revenue.toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};